import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { addContentToLayout } from 'actions/layoutManager';
import {
  createLayoutManagerForContentGrid,
  updateLayoutManagerForContentGrid,
  renderItemsForContentGrid
} from 'actions/contentGrid';

import {
  removeLayoutContents
} from 'actions/layoutManager';

import _ from 'lodash';
import cx from 'classnames';

import {
  Modal, Button, Dropdown, Form, Popover, Col, OverlayTrigger
} from 'react-bootstrap';
import { Sortable } from "@shopify/draggable";

import { EditorContext } from "components/Editor";
import StoryboardContext from "components/Storyboard/context";
import ManualSection from './manualSection';
import { AssetManager, AssetManagerModal } from 'components/AssetManager';

import FileService from 'services/file.service';

import styles from './sectionManager.module.scss'

const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

const SectionManager = ({ show, widgetProps, onClose }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    showAssetManager: false
  });

  const { id } = useParams();

  const storyboardContext = React.useContext(StoryboardContext);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const content = _.get(widgetProps, 'content', null);
  const layoutId = _.get(content, 'id', null);
  const mappings = _.get(content, 'mappings.items', []);
  const loadingMappings = _.get(content, 'mappings.loading', []);

  const empty = !layoutId;

  const maxOrder = useSelector(state => {
    var items = _.get(state, `${editorSession}.present.layoutManager.items`) || [];
    items = _.orderBy(items, 'order', 'desc');
    return _.get(items, '0.order') || 0;
  });

  const handleSave = (assets) => {
    if (!assets || assets.length === 0) {
      onClose();
      return;
    }

    if (empty || (!loadingMappings && _.size(mappings) == 0)) {
      let mappingRequest = (assets || []).map((item, index) => {
        var request = createMappingFromContent(item);

        request = {
          ...request,
          content: _.get(request, 'content.id'),
          fields: (request.fields || []).map(field => {
            if (field.name === 'icon') {
              return {
                ...field,
                content: _.get(field, 'content.id')
              }
            }

            if (field.name === 'author') {
              return {
                ...field,
                content: _.get(field, 'content')
              }
            }

            return field;
          })
        }

        return {
          ...request,
          order: maxOrder + index + 1
        };
      });

      var request = {
        mappings: mappingRequest,
        page: id,
        storyboard: storyboardContext.id,
        type: 'MANUAL'
      }

      if (!layoutId) {
        dispatch(createLayoutManagerForContentGrid(editorSession, widgetProps.uid, request));
      } else {
        dispatch(updateLayoutManagerForContentGrid(editorSession, widgetProps.uid, layoutId, request));
      }

      onClose();
    } else {
      let mappingRequest = (assets || []).map((item, index) => {
        var exists = mappings.some(m => _.get(m, 'content.asset.id') === _.get(item, 'asset.id')  && _.get(m, 'content.entityType') === item.asset.entityType) || mappings.some(m => _.get(m, 'content.id') === _.get(item, 'asset.id')  && _.get(m, 'content.entityType') === item.asset.entityType);
        if (exists) {
          return null;
        }

        const request = createMappingFromContent(item);
        return {
          ...request,
          order: maxOrder + index + 1
        };
      }).filter(item => item != null);

      dispatch(addContentToLayout(editorSession, widgetProps.uid, mappingRequest));

      setState(state => {
        return {
          ...state,
          showAssetManager: false
        }
      })
    }
  }

  const toggleAssetManager = () => {
    setState((state => {
      return {
        ...state,
        selectedAssets: [],
        showAssetManager: !state.showAssetManager
      }
    }))
  }
  
  const handleDeleteAssets = (assets) => {
    dispatch(removeLayoutContents(editorSession, widgetProps.uid, assets));
  }
  
  return (
    <Modal
      show={show}
      className={styles.sectionManagerModal}
      backdropClassName={styles.backdrop}
      centered
      backdrop="static"
      onHide={() => {}}
    >
      {empty &&
        <AssetManager
          show={show}
          onFinish={handleSave}
          onClose={onClose}
        />
      }
      {!empty &&
        <ManualSection
          widgetProps={widgetProps}
          onAddAssets={toggleAssetManager}
          onClose={onClose}
        />
      }
      <AssetManagerModal
        show={state.showAssetManager}
        multiple={true}
        onFinish={handleSave}
        onClose={toggleAssetManager}
        widgetProps={widgetProps}
        onDelete={handleDeleteAssets}
        editorSession={editorSession}
      />
    </Modal>
  )
}

const createField = (name, value) => {
  return {
    name: name,
    content: value,
    override: false,
    custom: false,
    desktop: true,
    mobile: true
  }
}

const createMappingFromContent = (content) => {
  if (!content) {
    return null;
  }

  const entityType = _.get(content, 'entityType');

  var mapping = {
    content: content
  }

  var name = _.get(content, 'asset.name');
  var description = _.get(content, 'asset.summary');
  var icon = _.get(content, 'asset.icon');

  var contentType = null;
  var fields = [];

  var author = _.get(content, 'asset.author.firstName') + _.get(content, 'asset.author.firstName');

  switch (entityType) {
    case 'storyboard': {
      mapping['type'] = 'INTERACTIVE_CONTENT';
      contentType = 'Interactive';
      icon = _.get(content, 'page.icon');
      mapping['content'] = content.page;
      break;
    }
    case 'page': {
      mapping['type'] = _.get(content, 'type', 'LANDING_PAGE');
      contentType = 'Page';
      break;
    }

    case 'asset': {
      mapping['type'] = 'ASSET';

      name = _.get(content, 'asset.name');
      description = _.get(content, 'asset.summary');
      var assetIcon = _.get(content, 'asset.icon');

      if (assetIcon) {
        icon = {
          id: assetIcon.id,
          type: assetIcon.type,
          url: assetIcon.key
        }
      }

      contentType = FileService.fileType(content.asset.metadata);
      var fileSize = FileService.formatSize(content.asset.metadata.content_length);

      fields = [
        ...fields,
        createField('fileSize', fileSize),
        createField('author', author)
      ]
      break;
    }
  }

  fields = [
    createField('name', name),
    createField('description', description),
    createField('icon', icon),
    createField('contentType', contentType),
    ...fields
  ];

  mapping['fields'] = fields;
  mapping['changes'] = true;

  return mapping;
}

export default SectionManager